import { useForm } from '@tanstack/react-form';
import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { z } from 'zod';

import { Alert, Button, Fade, Grid, Link, Stack, Typography } from 'gooey';
import { FormFieldError } from '#/components/forms/FormFieldError';
import { Page } from '#/components/pages/page';
import { PageSection } from '#/components/pages/page-section';
import fx from '#/fx';
import { useAnalytics } from '#/lib/analytics';
import { Content } from '#/lib/content';
import { canonicalLinkTag, seo } from '#/lib/head';

export const Route = createFileRoute('/_main/contact')({
  loader: async () => {
    const page = await fx.getPageContent({
      data: { key: 'contact' },
    });
    return { page };
  },
  head: ({ match }) => ({
    meta: seo({
      title: 'Contact Us',
      description: 'Get in touch with us for any questions or inquiries.',
      url: match.pathname,
    }),
    links: [canonicalLinkTag(match.pathname)],
  }),
  component: ContactPage,
});

function ContactPage() {
  const { page } = Route.useLoaderData();
  return (
    <Page>
      <PageSection>
        <Grid container spacing={{ xs: 2, sm: 4, md: 6 }}>
          <Grid size={{ xs: 12, sm: 12, md: 7 }}>
            <Fade in timeout={100}>
              {(transitionProps) => (
                <div {...transitionProps}>
                  <Typography variant='h2' component='h1' className='mb-8'>
                    <Content content={page.sections.hero.title} />
                  </Typography>
                  <ContactForm />
                </div>
              )}
            </Fade>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 5 }} className='md:justify-self-end'>
            <Stack
              className='text-center md:text-left'
              direction={{
                xs: 'column',
                sm: 'row',
                md: 'column',
              }}
              spacing={{
                xs: 4,
                sm: 6,
                md: 2,
              }}
              alignItems={{
                xs: 'center',
                sm: 'center',
                md: 'start',
              }}
              justifyContent={{
                xs: 'center',
                sm: 'center',
                md: 'start',
              }}
            >
              {page.sections.sidebar.emails.map(({ title, email }) => (
                <Fade key={email} in timeout={100}>
                  {(transitionProps) => (
                    <div {...transitionProps}>
                      <Typography variant='h6'>{title}</Typography>
                      <Link href={`mailto:${email}`} target='_blank' rel='noopener noreferrer'>
                        {email}
                      </Link>
                    </div>
                  )}
                </Fade>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </PageSection>
    </Page>
  );
}

/*
|------------------
| Internals
|------------------
*/

function ContactForm() {
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const analytics = useAnalytics();

  const form = useForm({
    defaultValues: {
      email: '',
      message: '',
    },
    onSubmit: async ({ value, formApi }) => {
      setShowSuccess(false);
      setShowError(false);
      try {
        await fx.sendContactMessage({
          data: {
            email: value.email,
            message: value.message,
          },
        });

        analytics.identify({ email: value.email });

        formApi.reset();
        setShowSuccess(true);
      } catch (error) {
        console.error(error);
        setShowError(true);
      }
    },
    validators: {
      onSubmit: contactDataSchema,
    },
  });

  return (
    <Stack
      component='form'
      id='contact-form'
      direction='column'
      spacing={1}
      justifyContent='start'
      alignContent='start'
      alignItems='start'
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {showSuccess && (
        <Alert className='w-full' severity='success' onClose={() => setShowSuccess(false)}>
          You successfully submitted the form. We will get back to you as soon as possible.
        </Alert>
      )}
      {showError && (
        <Alert className='w-full' severity='error' onClose={() => setShowError(false)}>
          There was an error submitting the form. Please try again later.
        </Alert>
      )}
      <form.Field
        name='email'
        validators={{
          onBlur: z.string().email('Must be valid email'),
        }}
      >
        {(field) => (
          <fieldset className='d-fieldset w-full'>
            <legend className='d-fieldset-legend text-lg pt-0'>Email Address</legend>
            <input
              type='text'
              className='d-input d-input-primary w-full'
              placeholder='Enter your email'
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
            <p className='d-fieldset-label'>
              <FormFieldError errors={field.state.meta.errors} />
            </p>
          </fieldset>
        )}
      </form.Field>
      <form.Field
        name='message'
        validators={{
          onBlur: z.string().min(6, 'Please add a message'),
        }}
      >
        {(field) => (
          <fieldset className='d-fieldset w-full'>
            <legend className='d-fieldset-legend text-lg pt-0'>How can we help?</legend>
            <textarea
              className='d-textarea d-textarea-primary w-full'
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="I'd to know how Joggr can help me with..."
            />
            <p className='d-fieldset-label'>
              <FormFieldError errors={field.state.meta.errors} />
            </p>
          </fieldset>
        )}
      </form.Field>
      <form.Subscribe
        selector={(state) => [
          state.canSubmit && !!state.values.email && !!state.values.message,
          state.isSubmitting,
          state.isSubmitted,
        ]}
      >
        {([canSubmit, isSubmitting]) => (
          <Button
            type='submit'
            variant='contained'
            className='w-full sm:w-auto'
            color='primary'
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            Send message
          </Button>
        )}
      </form.Subscribe>
    </Stack>
  );
}

const contactDataSchema = z.object({
  email: z.string().email('Must be valid email'),
  message: z.string().min(6, 'Please add a message, that is at least 6 characters long'),
});
